/**
 * Dedicated admin page for LLM models management: API keys per provider,
 * custom model IDs, and workflow assignments (blog writing, CMS coding, etc.).
 * Lives under System in the admin sidebar.
 */

import { useState, useEffect } from 'react';
import { Save, Sparkles, Eye, EyeOff, Info } from 'lucide-react';
import { getAIConfig, type AIConfig, type AIProvider } from '@/lib/cms';
import {
  getAIApiKeys,
  saveAIApiKeys,
  getAICustomModels,
  saveAICustomModels,
  getAIWorkflowAssignments,
  saveAIWorkflowAssignments,
  AI_WORKFLOW_IDS,
  type AIApiKeys,
  type AICustomModels,
  type AIWorkflowAssignments,
  type AIWorkflowId,
} from '@/lib/ai-config';
import {
  getModelsForProvider,
  BUILTIN_MODELS,
  getCostTierLabel,
  ALLOWED_OPENAI_MODEL_IDS,
  type ModelCostTier,
} from '@/lib/ai-models';

const WORKFLOW_LABELS: Record<AIWorkflowId, string> = {
  blog_writing: 'Blog writing',
  cms_page_coding: 'CMS page coding',
  article_context_suggestion: 'Article context suggestion (Make me lucky)',
  planning: 'Planning',
};

interface AdminLLMManagementProps {
  currentUserId: string;
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
}

export function AdminLLMManagement({
  currentUserId: _currentUserId,
  showNotify,
}: AdminLLMManagementProps): JSX.Element {
  void _currentUserId; // reserved for audit/future use
  const [aiConfig, setAIConfig] = useState<AIConfig | null>(null);
  const [aiConfigLoaded, setAIConfigLoaded] = useState(false);
  const [aiApiKeys, setAIApiKeys] = useState<AIApiKeys>({ openai: '', anthropic: '', gemini: '' });
  const [aiApiKeysLoaded, setAIApiKeysLoaded] = useState(false);
  const [savingApiKeys, setSavingApiKeys] = useState(false);
  const [showApiKey, setShowApiKey] = useState<Record<AIProvider, boolean>>({
    openai: false,
    anthropic: false,
    gemini: false,
  });
  const [aiCustomModels, setAICustomModels] = useState<AICustomModels>({
    openai: [],
    anthropic: [],
    gemini: [],
  });
  const [aiCustomModelsLoaded, setAICustomModelsLoaded] = useState(false);
  const [savingCustomModels, setSavingCustomModels] = useState(false);
  const [aiWorkflowAssignments, setAIWorkflowAssignments] = useState<AIWorkflowAssignments>({});
  const [aiWorkflowAssignmentsLoaded, setAIWorkflowAssignmentsLoaded] = useState(false);
  const [savingWorkflowAssignments, setSavingWorkflowAssignments] = useState(false);
  const [newCustomModel, setNewCustomModel] = useState<Record<AIProvider, string>>({
    openai: '',
    anthropic: '',
    gemini: '',
  });

  useEffect(() => {
    Promise.all([
      getAIApiKeys(),
      getAICustomModels(),
      getAIWorkflowAssignments(),
      getAIConfig(),
    ])
      .then(([keys, custom, assignments, legacyConfig]) => {
        if (legacyConfig) setAIConfig(legacyConfig);
        setAIConfigLoaded(true);
        // Pre-fill any provider key from legacy aiConfig when not already in aiApiKeys
        let mergedKeys = { ...keys };
        if (legacyConfig?.apiKey?.trim()) {
          const p = legacyConfig.provider;
          if (p && (!mergedKeys[p] || !String(mergedKeys[p]).trim())) {
            mergedKeys = { ...mergedKeys, [p]: legacyConfig.apiKey };
          }
        }
        setAIApiKeys(mergedKeys);
        setAICustomModels(custom);
        setAIWorkflowAssignments(assignments);
        setAIApiKeysLoaded(true);
        setAICustomModelsLoaded(true);
        setAIWorkflowAssignmentsLoaded(true);
      })
      .catch((err) => {
        console.error('[AdminLLMManagement] Error fetching LLM config:', err);
        setAIConfigLoaded(true);
        setAIApiKeysLoaded(true);
        setAICustomModelsLoaded(true);
        setAIWorkflowAssignmentsLoaded(true);
      });
  }, []);

  const handleSaveAIApiKeys = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingApiKeys(true);
    try {
      await saveAIApiKeys(aiApiKeys);
      showNotify?.('API keys saved successfully.', 'success');
    } catch (err) {
      console.error('[AdminLLMManagement] Error saving API keys:', err);
      showNotify?.('Failed to save API keys. Please try again.', 'error');
    } finally {
      setSavingApiKeys(false);
    }
  };

  const handleSaveAICustomModels = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingCustomModels(true);
    try {
      await saveAICustomModels(aiCustomModels);
      showNotify?.('Custom models saved successfully.', 'success');
    } catch (err) {
      console.error('[AdminLLMManagement] Error saving custom models:', err);
      showNotify?.('Failed to save custom models. Please try again.', 'error');
    } finally {
      setSavingCustomModels(false);
    }
  };

  const handleSaveAIWorkflowAssignments = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingWorkflowAssignments(true);
    try {
      await saveAIWorkflowAssignments(aiWorkflowAssignments);
      showNotify?.('Workflow assignments saved successfully.', 'success');
    } catch (err) {
      console.error('[AdminLLMManagement] Error saving workflow assignments:', err);
      showNotify?.('Failed to save workflow assignments. Please try again.', 'error');
    } finally {
      setSavingWorkflowAssignments(false);
    }
  };

  const handleImportFromLegacyAI = async () => {
    if (!aiConfig?.apiKey?.trim()) {
      showNotify?.('No legacy AI config with API key to import.', 'error');
      return;
    }
    setSavingApiKeys(true);
    setSavingWorkflowAssignments(true);
    try {
      const keys: AIApiKeys = { ...aiApiKeys, [aiConfig.provider]: aiConfig.apiKey };
      await saveAIApiKeys(keys);
      setAIApiKeys(keys);
      const assignment = { provider: aiConfig.provider, model: aiConfig.model };
      const assignments: AIWorkflowAssignments = {};
      for (const wid of AI_WORKFLOW_IDS) assignments[wid] = assignment;
      await saveAIWorkflowAssignments(assignments);
      setAIWorkflowAssignments(assignments);
      showNotify?.('Imported current AI settings into all workflows.', 'success');
    } catch (err) {
      console.error('[AdminLLMManagement] Error importing legacy AI config:', err);
      showNotify?.('Failed to import. Please try again.', 'error');
    } finally {
      setSavingApiKeys(false);
      setSavingWorkflowAssignments(false);
    }
  };

  const handleAddCustomModel = (provider: AIProvider) => {
    const value = newCustomModel[provider]?.trim();
    if (!value) return;
    const list = aiCustomModels[provider] ?? [];
    if (list.includes(value)) return;
    setAICustomModels((prev) => ({
      ...prev,
      [provider]: [...list, value],
    }));
    setNewCustomModel((prev) => ({ ...prev, [provider]: '' }));
  };

  const handleRemoveCustomModel = (provider: AIProvider, modelId: string) => {
    setAICustomModels((prev) => ({
      ...prev,
      [provider]: (prev[provider] ?? []).filter((id) => id !== modelId),
    }));
  };

  const setWorkflowAssignment = (workflowId: AIWorkflowId, provider: AIProvider, model: string) => {
    setAIWorkflowAssignments((prev) => ({
      ...prev,
      [workflowId]: { provider, model },
    }));
  };

  const loaded =
    aiApiKeysLoaded && aiCustomModelsLoaded && aiWorkflowAssignmentsLoaded;

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-violet-100 text-violet-600">
            <Sparkles size={18} />
          </span>
          <h1 className="text-xl font-bold text-gray-900">LLM Models Management</h1>
        </div>
        <p className="text-sm text-gray-500">
          API keys per provider, custom model IDs, and which provider+model to use per AI workflow
          (blog, CMS pages, article context suggestion, planning).
        </p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {!loaded ? (
          <div className="px-6 py-12 text-center text-sm text-gray-400">
            Loading LLM settings…
          </div>
        ) : (
          <div className="px-6 py-5 space-y-8">
            {/* Allowed models (OpenAI) */}
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 px-4 py-3">
              <h4 className="text-sm font-semibold text-gray-800">OpenAI — allowed models</h4>
              <p className="text-xs text-gray-600 mt-0.5">
                Only these models can be used in this project. Set the same list in your OpenAI project: Settings → Model usage → Allowed models.
              </p>
              <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-mono text-gray-700">
                {ALLOWED_OPENAI_MODEL_IDS.map((id) => (
                  <li key={id} className="flex items-center gap-1">
                    <span className="text-emerald-600" aria-hidden>✓</span>
                    {id}
                  </li>
                ))}
              </ul>
            </div>

            {/* Model guide: cost & use per model */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-gray-700">
                Model guide — cost &amp; use
              </h4>
              <p className="text-xs text-gray-500">
                Cheap = low cost for bulk/SEO; Moderate = balanced; Costly = best quality, higher price. Per 1M tokens (In / Cached / Out) as of March 6, 2026.
              </p>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left py-2 px-3 font-semibold text-gray-700">Provider</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-700">Model</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-700">Cost</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-700">Per 1M (In / Cached / Out)</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-700">Use</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(['openai', 'anthropic', 'gemini'] as const).map((provider) =>
                      (BUILTIN_MODELS[provider] ?? []).map((m) => {
                        const costPerM =
                          m.costPerMInput && m.costPerMOutput
                            ? m.costPerMCached
                              ? `$${m.costPerMInput} / $${m.costPerMCached} / $${m.costPerMOutput}`
                              : `$${m.costPerMInput} / — / $${m.costPerMOutput}`
                            : '—';
                        return (
                          <tr key={`${provider}-${m.value}`} className="border-b border-gray-100">
                            <td className="py-1.5 px-3 text-gray-600">
                              {provider === 'openai'
                                ? 'OpenAI'
                                : provider === 'anthropic'
                                  ? 'Anthropic'
                                  : 'Gemini'}
                            </td>
                            <td className="py-1.5 px-3 font-mono text-gray-800">{m.label}</td>
                            <td className="py-1.5 px-3">
                              {m.costTier ? (
                                <span
                                  className={
                                    m.costTier === 'low'
                                      ? 'text-emerald-600 font-medium'
                                      : m.costTier === 'medium'
                                        ? 'text-amber-600 font-medium'
                                        : 'text-rose-600 font-medium'
                                  }
                                >
                                  {getCostTierLabel(m.costTier as ModelCostTier)}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="py-1.5 px-3 text-gray-600 text-xs font-mono">{costPerM}</td>
                            <td className="py-1.5 px-3 text-gray-600">{m.useCase ?? '—'}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 1. API keys per provider */}
            <form onSubmit={(e) => void handleSaveAIApiKeys(e)}>
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-700">API keys (per provider)</h4>
                {(['openai', 'anthropic', 'gemini'] as const).map((provider) => (
                  <div key={provider} className="flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[200px] space-y-1">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {provider === 'openai'
                          ? 'OpenAI'
                          : provider === 'anthropic'
                            ? 'Anthropic (Claude)'
                            : 'Google Gemini'}
                      </label>
                      <div className="relative">
                        <input
                          type={showApiKey[provider] ? 'text' : 'password'}
                          value={aiApiKeys[provider] ?? ''}
                          onChange={(e) =>
                            setAIApiKeys((prev) => ({ ...prev, [provider]: e.target.value }))
                          }
                          placeholder={
                            provider === 'openai'
                              ? 'sk-…'
                              : provider === 'anthropic'
                                ? 'sk-ant-…'
                                : 'AIza…'
                          }
                          className="w-full rounded-lg border border-gray-300 bg-white pr-10 px-3 py-2 text-sm font-mono focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
                          autoComplete="off"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowApiKey((prev) => ({ ...prev, [provider]: !prev[provider] }))
                          }
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          aria-label={showApiKey[provider] ? 'Hide' : 'Show'}
                        >
                          {showApiKey[provider] ? (
                            <EyeOff size={14} />
                          ) : (
                            <Eye size={14} />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={savingApiKeys}
                    className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50"
                  >
                    <Save size={15} className={savingApiKeys ? 'animate-spin' : ''} />
                    {savingApiKeys ? 'Saving…' : 'Save API keys'}
                  </button>
                </div>
              </div>
            </form>

            {/* 2. Custom models per provider */}
            <form onSubmit={(e) => void handleSaveAICustomModels(e)}>
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-700">
                  Custom models (per provider)
                </h4>
                {(['openai', 'anthropic', 'gemini'] as const).map((provider) => (
                  <div key={provider} className="space-y-2">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                      {provider === 'openai'
                        ? 'OpenAI'
                        : provider === 'anthropic'
                          ? 'Anthropic'
                          : 'Gemini'}{' '}
                      custom model IDs
                    </label>
                    <div className="flex flex-wrap gap-2 items-center">
                      {(aiCustomModels[provider] ?? []).map((id) => (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-mono"
                        >
                          {id}
                          <button
                            type="button"
                            onClick={() => handleRemoveCustomModel(provider, id)}
                            className="text-gray-500 hover:text-red-600"
                            aria-label={`Remove ${id}`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      <input
                        type="text"
                        value={newCustomModel[provider] ?? ''}
                        onChange={(e) =>
                          setNewCustomModel((prev) => ({ ...prev, [provider]: e.target.value }))
                        }
                        onKeyDown={(e) =>
                          e.key === 'Enter' &&
                          (e.preventDefault(), handleAddCustomModel(provider))
                        }
                        placeholder="e.g. gpt-4-custom-xyz"
                        className="rounded border border-gray-300 px-2 py-1 text-sm font-mono w-40"
                      />
                      <button
                        type="button"
                        onClick={() => handleAddCustomModel(provider)}
                        className="text-sm text-violet-600 hover:text-violet-700 font-medium"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                ))}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={savingCustomModels}
                    className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50"
                  >
                    <Save size={15} className={savingCustomModels ? 'animate-spin' : ''} />
                    {savingCustomModels ? 'Saving…' : 'Save custom models'}
                  </button>
                </div>
              </div>
            </form>

            {/* Coding models guide — which model is best for coding */}
            <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-4 py-4">
              <h4 className="text-sm font-semibold text-gray-800">
                Coding models guide — which is best for coding
              </h4>
              <p className="text-xs text-gray-600 mt-1">
                Use this when assigning the <strong>CMS page coding</strong> workflow. Full guide: <code className="text-xs bg-slate-200 px-1 rounded">docs/OpenAI-refs/ai-models-coding.md</code>
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Cost per 1M tokens (Input / Cached / Output). As of March 6, 2026.
              </p>
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-xs border border-slate-200 rounded bg-white">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-100">
                      <th className="text-left py-2 px-3 font-semibold text-gray-700">Task</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-700">Recommended model</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-700">Cost (In / Cached / Out)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-slate-100"><td className="py-1.5 px-3">Complex coding, architecture, debugging</td><td className="py-1.5 px-3 font-mono font-medium">gpt-5.4-pro</td><td className="py-1.5 px-3 text-gray-600">$30 / — / $180</td></tr>
                    <tr className="border-b border-slate-100"><td className="py-1.5 px-3">Daily coding, scripts, refactoring</td><td className="py-1.5 px-3 font-mono font-medium">gpt-5-mini</td><td className="py-1.5 px-3 text-gray-600">$0.25 / $0.025 / $2</td></tr>
                    <tr className="border-b border-slate-100"><td className="py-1.5 px-3">Small scripts, cost-sensitive</td><td className="py-1.5 px-3 font-mono font-medium">gpt-4o-mini</td><td className="py-1.5 px-3 text-gray-600">$0.15 / $0.075 / $0.60</td></tr>
                    <tr className="border-b border-slate-100"><td className="py-1.5 px-3">Codex — repo understanding, multi-file, agentic</td><td className="py-1.5 px-3 font-mono font-medium">gpt-5.3-codex, gpt-5.1-codex-mini</td><td className="py-1.5 px-3 text-gray-600">$1.75/$0.175/$14 · $0.25/$0.025/$2</td></tr>
                  </tbody>
                </table>
              </div>
              <ul className="mt-3 text-xs text-gray-600 space-y-1 list-disc list-inside">
                <li><strong>Best overall:</strong> gpt-5.4-pro — strongest reasoning ($30 / $180 per 1M).</li>
                <li><strong>Best fast/cheap:</strong> gpt-5-mini — daily coding ($0.25 / $2 per 1M).</li>
                <li><strong>Lightweight:</strong> gpt-4o-mini — simple code ($0.15 / $0.60 per 1M).</li>
                <li><strong>Codex:</strong> gpt-5.3-codex $1.75/$14; gpt-5.1-codex-mini $0.25/$2 — CMS pages, agentic.</li>
              </ul>
              <p className="mt-2 text-xs text-gray-500">
                Example: Architecture → gpt-5.4-pro; Generate code → gpt-5-mini; CMS page coding → Codex or gpt-5-mini.
              </p>
            </div>

            {/* 3. Workflow assignment */}
            <form onSubmit={(e) => void handleSaveAIWorkflowAssignments(e)}>
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-700">Workflow assignment</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 pr-4 font-semibold text-gray-700">Workflow</th>
                        <th className="text-left py-2 pr-4 font-semibold text-gray-700">
                          Provider
                        </th>
                        <th className="text-left py-2 font-semibold text-gray-700">Model</th>
                      </tr>
                    </thead>
                    <tbody>
                      {AI_WORKFLOW_IDS.map((wid) => {
                        const a = aiWorkflowAssignments[wid];
                        const provider = a?.provider ?? 'openai';
                        const model =
                          a?.model ??
                          getModelsForProvider(provider, aiCustomModels[provider] ?? [])[0]
                            ?.value ??
                          '';
                        const models = getModelsForProvider(
                          provider,
                          aiCustomModels[provider] ?? []
                        );
                        return (
                          <tr key={wid} className="border-b border-gray-100">
                            <td className="py-2 pr-4 text-gray-700">{WORKFLOW_LABELS[wid]}</td>
                            <td className="py-2 pr-4">
                              <select
                                value={provider}
                                onChange={(e) => {
                                  const p = e.target.value as AIProvider;
                                  const opts = getModelsForProvider(p, aiCustomModels[p] ?? []);
                                  setWorkflowAssignment(wid, p, opts[0]?.value ?? '');
                                }}
                                className="rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
                              >
                                <option value="openai">OpenAI</option>
                                <option value="anthropic">Anthropic (Claude)</option>
                                <option value="gemini">Google Gemini</option>
                              </select>
                            </td>
                            <td className="py-2">
                              <select
                                value={model}
                                onChange={(e) => setWorkflowAssignment(wid, provider, e.target.value)}
                                className="rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-violet-500 focus:ring-1 focus:ring-violet-500 min-w-[180px]"
                              >
                                {models.map((m) => (
                                  <option key={m.value} value={m.value}>
                                    {m.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={savingWorkflowAssignments}
                    className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50"
                  >
                    <Save
                      size={15}
                      className={savingWorkflowAssignments ? 'animate-spin' : ''}
                    />
                    {savingWorkflowAssignments ? 'Saving…' : 'Save workflow assignments'}
                  </button>
                </div>
              </div>
            </form>

            {/* Import from legacy */}
            {aiConfigLoaded && (
              <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                <Info size={14} className="text-gray-400 shrink-0" />
                <p className="text-xs text-gray-500">
                  Legacy single config is still in Firestore (settings/aiConfig).
                </p>
                <button
                  type="button"
                  onClick={() => void handleImportFromLegacyAI()}
                  disabled={
                    savingApiKeys ||
                    savingWorkflowAssignments ||
                    !aiConfig?.apiKey?.trim()
                  }
                  className="text-xs font-medium text-violet-600 hover:text-violet-700 disabled:opacity-50"
                >
                  Import current AI settings into all workflows
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
